import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

const InvoiceForm = ({
  formData,
  clients,
  invoiceSeries,
  onInputChange,
  onItemChange,
  onAddItem,
  onRemoveItem,
  onSeriesSelect,
  onClientSelect,
  onAddDefaultService,
  calculateSubtotal,
  calculateVAT,
  calculateTotal,
  formatCurrency,
  onPreview,
  onCreateInvoice
}) => {
  const { t } = useLanguage();

  const defaultServices = [
    { description: t('onlineAppointments'), quantity: 1, price: 10 },
    { description: t('servicegestBasic'), quantity: 1, price: 150 }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={(e) => { e.preventDefault(); onCreateInvoice(); }} className="space-y-6">
        {/* Client Selection */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('clientName')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('selectClient')}</label>
              <select
                value={formData.clientId}
                onChange={(e) => onClientSelect(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                required
              >
                <option value="">{t('selectClient')}</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name} - {client.email}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('clientEmail')}</label>
              <input
                type="email"
                value={formData.clientEmail}
                readOnly
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 sm:text-sm"
              />
            </div>
          </div>
        </div>

        {/* Invoice Details */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('invoiceDetails')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('selectSeries')}</label>
              <select
                value={formData.seriesId || ''}
                onChange={(e) => onSeriesSelect(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              >
                <option value="">{t('selectSeries')}</option>
                {invoiceSeries.map(series => (
                  <option key={series.id} value={series.id}>
                    {series.prefix}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('invoiceNumber')}</label>
              <input
                type="text"
                value={formData.invoiceNumber}
                readOnly
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('vatRate')}</label>
              <select
                name="vatRate"
                value={formData.vatRate}
                onChange={onInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              >
                <option value={0}>0%</option>
                <option value={5}>5%</option>
                <option value={9}>9%</option>
                <option value={21}>21%</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('issueDate')}</label>
              <input
                type="date"
                name="issueDate"
                value={formData.issueDate}
                onChange={onInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('dueDate')}</label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={onInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              />
            </div>
          </div>
        </div>

        {/* Invoice Items */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">{t('items')}</h3>
            <div className="flex space-x-2">
              {defaultServices.map((service, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => onAddDefaultService(service)}
                  className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-md hover:bg-gray-200"
                >
                  {service.description}
                </button>
              ))}
              <button
                type="button"
                onClick={() => onAddDefaultService({ description: t('customProduct'), quantity: 1, price: 0 })}
                className="text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded-md hover:bg-purple-200"
              >
                {t('addCustomProduct')}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('description')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('quantity')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('unitPrice')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('total')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {formData.items.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => onItemChange(index, 'description', e.target.value)}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                        placeholder={t('description')}
                        required
                      />
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => onItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                        min="0"
                        step="0.01"
                        required
                      />
                    </td>
                    <td className="px-6 py-4">
                      <input
                        type="number"
                        value={item.price}
                        onChange={(e) => onItemChange(index, 'price', parseFloat(e.target.value) || 0)}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                        min="0"
                        step="0.01"
                        required
                      />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatCurrency(item.quantity * item.price)}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        type="button"
                        onClick={() => onRemoveItem(index)}
                        className="text-red-600 hover:text-red-900"
                        disabled={formData.items.length === 1}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <button
              type="button"
              onClick={onAddItem}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-purple-700 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
              {t('addItem')}
            </button>
          </div>
        </div>
        {/* Invoice Summary */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('invoiceSummary')}</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">{t('subtotal')}:</span>
              <span className="text-sm font-medium">{formatCurrency(calculateSubtotal())}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">{t('vatRate')} ({formData.vatRate}%):</span>
              <span className="text-sm font-medium">{formatCurrency(calculateVAT())}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="text-base font-medium">{t('totalAmount')}:</span>
              <span className="text-base font-bold">{formatCurrency(calculateTotal())}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onPreview}
            className="px-6 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400"
          >
            {t('preview')}
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-purple-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-purple-700"
          >
            {t('createInvoice')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InvoiceForm;
